/**
 * Lazy bundle of markdown under docs/. Glob keys build a path index at startup;
 * file contents load on demand via fetchDocsPage().
 *
 * Optional `NN-` prefix on filenames (e.g. `01-menu-groups.md`) sets sibling
 * order in the docs nav; the prefix is stripped from the logical URL path.
 */

export interface DocsPageIndexEntry {
  /** Route key: e.g. en, en/table-order, en/table-order/configuration/menu-groups */
  logicalPath: string;
  /** Directory containing this file, relative to docs/ — used for relative .md links */
  folderPath: string;
  /**
   * When the source file used a `NN-` basename prefix, this is the numeric order
   * (1–99). Used to sort siblings in the side nav; null for unordered pages.
   */
  docOrder: number | null;
}

export interface DocsPageMeta extends DocsPageIndexEntry {
  raw: string;
}

/** Strip leading `01-` style order prefix from a doc filename segment (not including .md). */
export function stripDocFilenameOrderPrefix(basename: string): { order: number | null; slug: string } {
  const m = /^(\d{2})-(.+)$/.exec(basename);
  if (!m) {
    return { order: null, slug: basename };
  }
  const n = parseInt(m[1], 10);
  if (Number.isNaN(n)) {
    return { order: null, slug: basename };
  }
  return { order: n, slug: m[2] };
}

function normalizeGlobKey(key: string): string {
  const idx = key.indexOf('/docs/');
  const slice = idx >= 0 ? key.slice(idx + '/docs/'.length) : key.replace(/^.*\/docs\//, '');
  return slice.replace(/^\//, '');
}

function pathToLogical(relativeToDocs: string): DocsPageIndexEntry | null {
  if (!relativeToDocs.endsWith('.md')) {
    return null;
  }
  const withoutMd = relativeToDocs.slice(0, -3);
  if (withoutMd.endsWith('/README')) {
    const logicalPath = withoutMd.slice(0, -'/README'.length);
    return { logicalPath, folderPath: logicalPath, docOrder: null };
  }
  const lastSlash = withoutMd.lastIndexOf('/');
  const folderPath = lastSlash >= 0 ? withoutMd.slice(0, lastSlash) : '';
  const basename = lastSlash >= 0 ? withoutMd.slice(lastSlash + 1) : withoutMd;
  const { order, slug } = stripDocFilenameOrderPrefix(basename);
  const logicalPath = folderPath ? `${folderPath}/${slug}` : slug;
  return { logicalPath, folderPath: folderPath || '', docOrder: order };
}

const rawLoaders = import.meta.glob<string>('../docs/**/*.md', {
  query: '?raw',
  import: 'default',
});

const pathIndex = new Map<string, DocsPageIndexEntry>();
const loaderByPath = new Map<string, () => Promise<string>>();
const contentCache = new Map<string, DocsPageMeta>();

for (const [key, loader] of Object.entries(rawLoaders)) {
  const rel = normalizeGlobKey(key);
  const parsed = pathToLogical(rel);
  if (!parsed) {
    continue;
  }
  pathIndex.set(parsed.logicalPath, parsed);
  loaderByPath.set(parsed.logicalPath, loader);
}

export function hasDocsPage(logicalPath: string): boolean {
  return pathIndex.has(logicalPath);
}

/** @deprecated Use hasDocsPage for existence checks or fetchDocsPage for content. */
export function getDocsPage(logicalPath: string): DocsPageMeta | undefined {
  return contentCache.get(logicalPath);
}

export function getDocsPageIndex(logicalPath: string): DocsPageIndexEntry | undefined {
  return pathIndex.get(logicalPath);
}

export function getDocsLogicalPaths(): string[] {
  return [...pathIndex.keys()].sort((a, b) => a.localeCompare(b));
}

export function getCachedDocsPage(logicalPath: string): DocsPageMeta | undefined {
  return contentCache.get(logicalPath);
}

export async function fetchDocsPage(logicalPath: string): Promise<DocsPageMeta | undefined> {
  const cached = contentCache.get(logicalPath);
  if (cached) {
    return cached;
  }

  const index = pathIndex.get(logicalPath);
  const loader = loaderByPath.get(logicalPath);
  if (!index || !loader) {
    return undefined;
  }

  const raw = await loader();
  const page: DocsPageMeta = { ...index, raw };
  contentCache.set(logicalPath, page);
  return page;
}

/** Locales that have at least one doc page (first path segment). */
export function getDocsLocales(): string[] {
  const locales = new Set<string>();
  for (const p of pathIndex.keys()) {
    const seg = p.split('/')[0];
    if (seg) {
      locales.add(seg);
    }
  }
  return [...locales].sort();
}

export function localeHasDocs(locale: string): boolean {
  for (const p of pathIndex.keys()) {
    if (p === locale || p.startsWith(`${locale}/`)) {
      return true;
    }
  }
  return false;
}
